import { Alert, Button, Menu, MenuItem, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useCallback, useState } from "react";

import Filters, { IApplyDataType } from "./filters";

import Table, { createData } from "@/components/app/ContentTable";
import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { useContentsList } from "@/api/hooks/useContents";
import { IContentsParams } from "@/api/types/content";

const ContentList: React.FC = () => {
    const createAccess = useCheckPermissionAccess(PagesAccess.CONTENT, OperationAccess.CREATE);
    // const deleteAccess = useCheckPermissionAccess(PagesAccess.CONTENT, OperationAccess.DELETE);
    // const showAccess = useCheckPermissionAccess(PagesAccess.CONTENT, OperationAccess.LIST);

    const [params, setParams] = useState<IContentsParams>({
        search: "",
        skip: 0,
        take: 10,
    });

    const { data: ContentData, isLoading, isFetching, isPending } = useContentsList(params);

    const Data = ContentData?.data.items.map((item) => createData(item));

    const [anchorEl, setOpen] = useState<HTMLElement | null>(null);

    const open = Boolean(anchorEl);

    const handleApply = (data: IApplyDataType) => {
        setParams((prev) => {
            return {
                ...prev,
                search: data.search || "",
                filter: { ...data.filter },
            };
        });
    };

    const handleChange = useCallback(({ page, rowsPerPage }: { page?: number, rowsPerPage?: number }) => {
        setParams((prev) => {
            return {
                ...prev,
                skip: Number(page) * Number(rowsPerPage),
                take: Number(rowsPerPage),
            };
        });
    }, []);

    const handleClose = () => setOpen(null);
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => setOpen(e.currentTarget);

    return (
        <>
            <DashboardHeader
                title="Content"
                titleSuffix={
                    createAccess && (
                        <>
                            <Button
                                aria-controls={open ? "basic-menu" : undefined}
                                aria-expanded={open ? "true" : undefined}
                                aria-haspopup="true"
                                color="primary"
                                endIcon={<KeyboardArrowDownIcon />}
                                id="basic-button"
                                variant="contained"
                                onClick={handleClick}
                            >
                                Add New Content
                            </Button>
                            <Menu anchorEl={anchorEl} id="basic-menu" open={open} onClose={handleClose}>
                                <MenuItem
                                    component={Link}
                                    to={ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.ADD_NEW_MOVIE.ROOT.ABSOLUTE}
                                >
                                    Create Movie
                                </MenuItem>
                                <MenuItem
                                    component={Link}
                                    to={ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.ADD_NEW_SERIES.ROOT.ABSOLUTE}
                                >
                                    Create Series
                                </MenuItem>
                            </Menu>
                        </>
                    )
                }
            >
                <Typography className="p">Create the content with all the details and see the list of content.</Typography>
                <Typography className="p">Sort the content by list and create new videos or series.</Typography>
                <Typography className="p">
                    Additionally, you can view the table of contents and search through the content library.
                </Typography>
                <Alert severity="info" sx={{ marginTop: "1em" }}>
                    Note: First, you have to create tags and genres before creating the content!
                </Alert>
            </DashboardHeader>

            <Filters onApply={handleApply} />
            <DashboardContent>
                <Table
                    data={Data || []}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    isPending={isPending}
                    page={Number(params.skip) / Number(params.take)}
                    rowsPerPage={Number(params.take)}
                    totalItems={ContentData ? ContentData.data.pagination.totalItems : 0}
                    onChange={handleChange}
                />
            </DashboardContent>
        </>
    );
};

export default ContentList;
